/**
 * PreparedDropsDraftsList - drafts and sent drops list with actions.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';
import type { FC } from 'react';

import type { BulkUpdatePreparedDropsPayload } from '@/hooks';
import { ConfirmDialog } from '@/components/ui';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { CopyAsDraftModal, DuplicateDraftModal } from '@/features/smswiser-campaigns/components/modals';
import ScaleDropModal from '@/features/smswiser-campaigns/components/ScaleDropModal';
import { EditSentDropModal } from '@/features/smswiser-campaigns/components/modals';
import { MultiCountryBulkEditModal } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit';
import { MultiCountrySentScheduledEditModal } from '@/features/smswiser-campaigns/components/MultiCountrySentScheduledEdit';
import { isScheduledSentPrepared } from '@/features/smswiser-campaigns/components/MultiCountrySentScheduledEdit/shared';
import { ApplyCopiedConfigSentPreviewModal } from '@/features/smswiser-campaigns/components/modals/sent/ApplyCopiedConfigSentPreviewModal';
import { preparedDropToDropsWithStatsRow } from '@/features/smswiser-campaigns/utils/preparedDropToScaleRow';

import {
  ColumnHeadersRow,
  CreativeFillBanner,
  DraftRowOverlay,
  DraftsActionsBar,
  ListHeader,
  PreparedDropsDraftsListRow,
  SentActionsBar,
  SentSectionsList,
  SortableDraftRow,
  useCreativeFilling,
  usePreparedDropsDraftsList,
} from './PreparedDropsDraftsList/index';
import { buildApplyPayload } from './PreparedDropsDraftsList/shared/apply';
import { useToolCapabilities } from '@/hooks/smswiser/tool-stats';
import { useCampaignsForCountryKeys } from './PreparedDropsDraftsList/hooks/useCampaignsForCountryKeys';
import {
  buildCampaignsByCountryFromDrafts,
  mergeCampaignLists,
} from './PreparedDropsDraftsList/shared/multiCountryCatalogCampaigns';
import {
  draftsHeaderGridWithDragAndCountryStyle,
  draftsHeaderGridWithDragStyle,
  draftsHeaderGridWithCountryStyle,
  draftsHeaderGridStyle,
  draftsRowGridWithCountryStyle,
} from './PreparedDropsDraftsList/shared/grid';
import { money } from './PreparedDropsDraftsList/shared/utils';
import { useDropApplyStore } from '@/stores/drop-apply.store';
import { useToast } from '@/providers/ToastProvider';
import { ResendKilledControls } from '@/features/resend-killed-drops';

export type { PreparedDropsDraftsListProps } from './PreparedDropsDraftsList/index';

export const PreparedDropsDraftsList: FC<import('./PreparedDropsDraftsList/index').PreparedDropsDraftsListProps> = (props) => {
  const {
    countryKey,
    wiserId,
    campaigns = [],
    workDate,
    isLoading,
    gateways,
    leadsLists,
    tlds,
    creative,
    isBusy,
    sendingDropId,
    killingDropIds = [],
    showPlusOne,
    onSend,
    onBulkUpdate,
    onBulkSaveAndSend,
    onDelete: _onDelete,
    onDuplicateDraft,
    onKillSent,
    onCopyAsDraft,
    onBeforeEditSent,
    onSaveSentEdit,
    onAddSender,
    onAddText,
    onDeleteSender,
    onDeleteText,
    onDeleteTextsBulk,
    onReorder,
    onSaveAsPreset,
    onCreateSmswiserDraft,
    perfTagsQuery,
    tagsByTextId,
    textPerformanceTagColors,
    highlightPreparedDropId,
    onInvestigationFocusConsumed: _onInvestigationFocusConsumed,
    listReturnTo,
    showCountryColumn = false,
    disableDraftReorder = false,
    gatewaysWithCountry = [],
    leadsWithCountry = [],
  } = props;

  const effectiveReorder = disableDraftReorder ? undefined : onReorder;
  const listBackTo = listReturnTo ?? `/smswiser/${encodeURIComponent(countryKey)}`;
  const draftsHeaderStyle =
    effectiveReorder
      ? showCountryColumn
        ? draftsHeaderGridWithDragAndCountryStyle
        : draftsHeaderGridWithDragStyle
      : showCountryColumn
        ? draftsHeaderGridWithCountryStyle
        : draftsHeaderGridStyle;
  const draftRowGridStyle = showCountryColumn ? draftsRowGridWithCountryStyle : undefined;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const state = usePreparedDropsDraftsList(props);
  const creativeFilling = useCreativeFilling(props.countryKey);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  // Kill is the ONE action on this page with no undo — SMSWiser has no resume,
  // restart or un-stop, so a stopped drop stays stopped. Everything else here
  // is recoverable from the Undo button; this asks instead.
  const [killConfirmOpen, setKillConfirmOpen] = useState(false);
  const [applyCopiedPreviewOpen, setApplyCopiedPreviewOpen] = useState(false);
  const [stagedApplyPayload, setStagedApplyPayload] = useState<BulkUpdatePreparedDropsPayload | null>(null);
  const [initialApplyForEditModal, setInitialApplyForEditModal] =
    useState<BulkUpdatePreparedDropsPayload | null>(null);
  const hasCopiedValues = useDropApplyStore((s) => s.hasValues);
  const { error: toastError } = useToast();
  // Hide kill/edit on sent drops until the SMSWiser REST API supports them.
  const { data: toolCapabilities } = useToolCapabilities();
  const canStopDrop = toolCapabilities?.supportsStopDrop === true;
  const canEditDrop = toolCapabilities?.supportsEditDrop === true;

  const handleApply = useCallback(() => {
    const v = useDropApplyStore.getState().values;
    if (!v) return;
    const payload = buildApplyPayload({ values: v, campaigns, gateways });
    if (!payload) {
      toastError('Apply failed', 'Could not resolve campaign or gateway. Copy again from a drop.');
      return;
    }
    onBulkUpdate(state.selectedIds, payload);
  }, [campaigns, gateways, onBulkUpdate, state.selectedIds, toastError]);

  const handleOpenApplyCopiedPreview = useCallback(() => {
    if (!useDropApplyStore.getState().hasValues()) {
      toastError('Nothing to apply', 'Copy config from a stats drop first.');
      return;
    }
    const v = useDropApplyStore.getState().values;
    if (!v) return;
    const payload = buildApplyPayload({ values: v, campaigns, gateways });
    if (!payload) {
      toastError('Apply failed', 'Could not resolve campaign or gateway. Copy again from a drop.');
      return;
    }
    setStagedApplyPayload(payload);
    setApplyCopiedPreviewOpen(true);
  }, [campaigns, gateways, toastError]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !effectiveReorder) {
        setActiveDragId(null);
        return;
      }
      const draftIds = state.displayedDrops.filter((d) => d.status === 'DRAFT').map((d) => d.id);
      const oldIndex = draftIds.indexOf(active.id as string);
      const newIndex = draftIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) {
        setActiveDragId(null);
        return;
      }
      const reordered = arrayMove(draftIds, oldIndex, newIndex);
      const updates = reordered.map((id, i) => ({ id, orderIndex: i }));
      const reorderPromise = effectiveReorder(updates);
      requestAnimationFrame(() => setActiveDragId(null));
      await reorderPromise;
    },
    [effectiveReorder, state.displayedDrops],
  );

  const {
    activeTab,
    sentSection,
    draftDrops,
    sentDrops,
    sentCountBySection,
    sentBySection,
    displayedDrops,
    selectedIds,
    selectedSet,
    hasSelection,
    allSelected,
    selectedSentDrops,
    canKillOrEdit,
    selectedWithSentId,
    editingSentDrops,
    searchQuery,
    tagFilter,
    tagFilterOptions,
    tagColors,
    tagsLoading,
    staticTagFilters,
    staticTagFilterOptions,
    staticTagColors,
    bulkSendStatus,
    sortBy,
    sortDir,
    catalog,
    tagsForDrop,
    countryKeysFilter,
    setCountryKeysFilter,
    scheduledGroupByCountry,
    setScheduledGroupByCountry,
    smswiserDraftsOnly,
    setSmswiserDraftsOnly,
    countryFilterOptions,
    setSearchQuery,
    setTagFilter,
    setStaticTagFilters,
    setEditingSentDropIds,
    setShowCopyAsDraft,
    setShowDuplicateDraft,
    setShowBulkEdit,
    showMultiCountryEdit,
    setShowMultiCountryEdit,
    multiCountrySentEditIds,
    setMultiCountrySentEditIds,
    requestOpenBulkEdit,
    handleSort,
    toggleSelected,
    clearSelection,
    handleToggleAll,
    handleToggleSectionAll,
    handleSentSectionChange,
    switchToDrafts,
    switchToSent,
    handleKillSent,
    handleEditSent,
    handleCopyAsDraftClick,
    handleDuplicateDraftClick,
    handleDuplicateDraftConfirm,
    handleCopyAsDraftConfirm,
    handleBulkSend,
    handleDelete,
    handleToggleScheduledSendTimeSort,
    onDraftRowPointerDown,
    onDraftRowPointerEnter,
    consumeDraftRowClickSuppress,
  } = state;

  const scaleModalCountryKey = useMemo(() => {
    if (showCountryColumn && state.showBulkEdit && selectedIds.length > 0) {
      const sel = draftDrops.filter((d) => selectedIds.includes(d.id));
      const k = sel[0]?.countryKey;
      if (k) return k;
    }
    return countryKey;
  }, [showCountryColumn, state.showBulkEdit, selectedIds, draftDrops, countryKey]);

  const editModalCountryKey = useMemo(() => {
    if (showCountryColumn && editingSentDrops.length > 0) {
      const k = editingSentDrops[0]?.countryKey;
      if (k) return k;
    }
    return countryKey;
  }, [showCountryColumn, editingSentDrops, countryKey]);

  const modalGateways = useMemo(() => {
    if (showCountryColumn && state.showBulkEdit && gatewaysWithCountry.length > 0) {
      return gatewaysWithCountry.filter((g) => g.countryKey === scaleModalCountryKey);
    }
    return gateways;
  }, [showCountryColumn, state.showBulkEdit, gatewaysWithCountry, scaleModalCountryKey, gateways]);

  const modalLeadsLists = useMemo(() => {
    if (showCountryColumn && state.showBulkEdit && leadsWithCountry.length > 0) {
      return leadsWithCountry.filter((l) => l.countryKey === scaleModalCountryKey);
    }
    return leadsLists;
  }, [showCountryColumn, state.showBulkEdit, leadsWithCountry, scaleModalCountryKey, leadsLists]);

  const editModalGateways = useMemo(() => {
    if (showCountryColumn && editingSentDrops.length > 0 && gatewaysWithCountry.length > 0) {
      return gatewaysWithCountry.filter((g) => g.countryKey === editModalCountryKey);
    }
    return gateways;
  }, [showCountryColumn, editingSentDrops.length, gatewaysWithCountry, editModalCountryKey, gateways]);

  const catalogCountryKeys = useMemo(() => {
    const set = new Set<string>();
    for (const g of gatewaysWithCountry) set.add(g.countryKey);
    for (const d of draftDrops) {
      if (d.status === 'DRAFT') set.add(d.countryKey);
    }
    return [...set];
  }, [gatewaysWithCountry, draftDrops]);

  const apiCampaignsByCountry = useCampaignsForCountryKeys(catalogCountryKeys, showCountryColumn);

  const multiCountryCatalog = useMemo(() => {
    const emptyEntry = () => ({
      gateways: [] as Array<{ id: number; name: string; pricePerSms?: number }>,
      campaigns: [] as Array<{ id: number; name: string }>,
      tlds: [] as Array<{ value: string; displayName: string }>,
      leadsLists: [] as Array<{ id: number; name: string }>,
    });
    const result: Record<string, ReturnType<typeof emptyEntry>> = {};
    for (const g of gatewaysWithCountry) {
      const entry = result[g.countryKey] ?? emptyEntry();
      entry.gateways.push({ id: g.id, name: g.name, pricePerSms: g.pricePerSms ?? undefined });
      result[g.countryKey] = entry;
    }
    for (const d of draftDrops) {
      if (d.status !== 'DRAFT') continue;
      if (!result[d.countryKey]) result[d.countryKey] = emptyEntry();
    }
    for (const d of props.drops) {
      if (d.status !== 'SENT' || !isScheduledSentPrepared(d)) continue;
      if (!result[d.countryKey]) result[d.countryKey] = emptyEntry();
    }
    const campaignsByCountry = buildCampaignsByCountryFromDrafts(draftDrops);
    const tldOptions = (tlds ?? []).map((t) => ({ value: t.value, displayName: t.displayName }));
    for (const ck of Object.keys(result)) {
      const fromDrafts = campaignsByCountry[ck] ?? [];
      const fromApi = showCountryColumn ? apiCampaignsByCountry[ck] ?? [] : [];
      const tabList = ck === countryKey && campaigns.length > 0 ? campaigns : [];
      result[ck]!.campaigns = mergeCampaignLists(fromDrafts, fromApi, tabList);
      result[ck]!.tlds = tldOptions;
      result[ck]!.leadsLists = leadsWithCountry
        .filter((l) => l.countryKey === ck)
        .map((l) => ({ id: l.id, name: l.name }));
    }
    return result;
  }, [
    gatewaysWithCountry,
    tlds,
    campaigns,
    draftDrops,
    props.drops,
    countryKey,
    showCountryColumn,
    apiCampaignsByCountry,
    leadsWithCountry,
  ]);

  const handleApplyCopiedConfirmEdit = useCallback(() => {
    if (!stagedApplyPayload || selectedWithSentId.length === 0) return;
    setInitialApplyForEditModal(stagedApplyPayload);
    setEditingSentDropIds(selectedWithSentId.map((d) => d.id));
    setApplyCopiedPreviewOpen(false);
    setStagedApplyPayload(null);
  }, [stagedApplyPayload, selectedWithSentId, setEditingSentDropIds]);

  // Only selected drafts NOT already staged on SMSWiser can be created — if every
  // selected draft is already a SMSWiser draft, the "Create draft" button is hidden.
  const selectedUnstagedDraftIds = useMemo(
    () =>
      draftDrops
        .filter((d) => selectedIds.includes(d.id) && d.draftDropUniqueId == null)
        .map((d) => d.id),
    [draftDrops, selectedIds],
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <div className="sticky top-0 z-10 shrink-0 flex flex-col bg-background">
        <CreativeFillBanner countryKey={countryKey} />
        <ListHeader
          activeTab={activeTab}
          sentSection={sentSection}
          showDragHandle={activeTab === 'drafts' && !!effectiveReorder}
          draftCount={draftDrops.length}
          sentCount={sentDrops.length}
          sentCountBySection={sentCountBySection}
          showPlusOne={showPlusOne ?? false}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSwitchToDrafts={switchToDrafts}
          onSwitchToSent={switchToSent}
          onSentSectionChange={handleSentSectionChange}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          tagFilter={tagFilter}
          tagFilterOptions={tagFilterOptions}
          tagColors={tagColors}
          tagsLoading={tagsLoading}
          onTagFilterChange={setTagFilter}
          staticTagFilters={staticTagFilters}
          staticTagFilterOptions={staticTagFilterOptions}
          staticTagColors={staticTagColors}
          onStaticTagFiltersChange={setStaticTagFilters}
          showCountryFilter={showCountryColumn && (countryFilterOptions?.length ?? 0) > 0}
          countryFilterOptions={countryFilterOptions}
          selectedCountryKeys={countryKeysFilter}
          onCountryKeysChange={setCountryKeysFilter}
          smswiserDraftsOnly={smswiserDraftsOnly}
          onToggleSmswiserDraftsOnly={() => setSmswiserDraftsOnly((v: boolean) => !v)}
          showScheduledSortToolbar={
            activeTab === 'sent' && sentSection === 'scheduled' && sentCountBySection.scheduled > 0
          }
          scheduledSortBy={sortBy}
          scheduledSortDir={sortDir}
          onToggleScheduledSendTimeSort={handleToggleScheduledSendTimeSort}
        />

        {activeTab === 'drafts' && (
          <DraftsActionsBar
            hasSelection={hasSelection}
            selectedCount={selectedIds.length}
            totalCount={displayedDrops.length}
            allSelected={allSelected}
            isBusy={isBusy}
            bulkSendRunning={bulkSendStatus.running}
            bulkSendStatus={bulkSendStatus}
            onToggleAll={handleToggleAll}
            onDelete={handleDelete}
            onBulkEdit={requestOpenBulkEdit}
            onDuplicate={onDuplicateDraft ? handleDuplicateDraftClick : undefined}
            onSaveAsPreset={
              onSaveAsPreset
                ? () => onSaveAsPreset(draftDrops.filter((d) => selectedIds.includes(d.id)))
                : undefined
            }
            onApply={handleApply}
            hasCopiedValues={hasCopiedValues()}
            onBulkSend={handleBulkSend}
            onCreateSmswiserDraft={
              onCreateSmswiserDraft && selectedUnstagedDraftIds.length > 0
                ? () => onCreateSmswiserDraft(selectedUnstagedDraftIds)
                : undefined
            }
            smswiserDraftCount={selectedUnstagedDraftIds.length}
            onClearSelection={clearSelection}
            listGroupByCountry={scheduledGroupByCountry}
            onListGroupByCountryChange={
              showCountryColumn && (countryFilterOptions?.length ?? 0) > 0
                ? setScheduledGroupByCountry
                : undefined
            }
          />
        )}

        {activeTab === 'sent' && (onKillSent || onSaveSentEdit || onCopyAsDraft || onSaveAsPreset) && (
          <SentActionsBar
            hasSelection={hasSelection}
            selectedCount={selectedSentDrops.length}
            totalCount={displayedDrops.length}
            allSelected={allSelected}
            isBusy={isBusy}
            canKillOrEdit={canKillOrEdit || (!!onSaveAsPreset && hasSelection)}
            selectedWithSentIdCount={selectedWithSentId.length}
            onToggleAll={handleToggleAll}
            onCopyAsDraft={onCopyAsDraft ? handleCopyAsDraftClick : undefined}
            onKill={onKillSent && canStopDrop ? () => setKillConfirmOpen(true) : undefined}
            onEdit={onSaveSentEdit && canEditDrop ? handleEditSent : undefined}
            onSaveAsPreset={
              onSaveAsPreset && selectedSentDrops.length > 0
                ? () => onSaveAsPreset(selectedSentDrops)
                : undefined
            }
            resendSlot={
              <ResendKilledControls drops={selectedSentDrops} disabled={isBusy} onDone={clearSelection} />
            }
            onClearSelection={clearSelection}
            hasCopiedValues={hasCopiedValues()}
            onApplyCopied={onSaveSentEdit ? handleOpenApplyCopiedPreview : undefined}
            listGroupByCountry={scheduledGroupByCountry}
            onListGroupByCountryChange={
              showCountryColumn && (countryFilterOptions?.length ?? 0) > 0
                ? setScheduledGroupByCountry
                : undefined
            }
          />
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border">
        <div className="min-w-max">
          {activeTab === 'sent' ? (
            isLoading ? (
              <div className="p-4 text-sm text-muted-foreground">Loading…</div>
            ) : (
              <SentSectionsList
                sentBySection={sentBySection}
                sentSection={sentSection}
                highlightPreparedDropId={highlightPreparedDropId}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                selectedSet={selectedSet}
                onToggleSelected={toggleSelected}
                onToggleSectionAll={handleToggleSectionAll}
                catalog={catalog}
                tagsForDrop={tagsForDrop}
                tagColors={tagColors}
                showCountryColumn={showCountryColumn}
                rowGridStyle={draftRowGridStyle}
                isBusy={isBusy}
                sendingDropId={sendingDropId}
                killingDropIds={killingDropIds}
                returnTo={listBackTo}
                onSend={onSend}
                onEdit={(id) => {
                  if (id) {
                    toggleSelected(id, true);
                    requestOpenBulkEdit();
                  }
                }}
                onEditSent={
                  onSaveSentEdit
                    ? async (d) => {
                        if (onBeforeEditSent) {
                          const ok = await onBeforeEditSent(d);
                          if (!ok) return;
                        }
                        setEditingSentDropIds([d.id]);
                      }
                    : undefined
                }
              />
            )
          ) : (
            <div className="grid w-full gap-x-3 [&>*]:col-span-full" style={draftsHeaderStyle}>
              <ColumnHeadersRow
                activeTab="drafts"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                showDragHandle={!!effectiveReorder}
                showCountryColumn={showCountryColumn}
              />
              {isLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Loading drafts…</div>
              ) : displayedDrops.length === 0 ? (
                <div className="col-span-full border-b border-border/30 p-4 text-sm text-muted-foreground">
                  {searchQuery.trim() || tagFilter || staticTagFilters.length > 0
                    ? 'No matches for your search or filter. Try a different term or tag.'
                    : 'No drafts yet. Use "Prepare drafts" to create some.'}
                </div>
              ) : effectiveReorder ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={displayedDrops.filter((d) => d.status === 'DRAFT').map((d) => d.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <>
                      {displayedDrops.map((drop) => {
                        const price = catalog.gatewayPricePerSms(drop);
                        const costPerSms = price > 0 ? money(price) : null;
                        const totalCost = price > 0 ? money(drop.amount * price) : null;
                        const rowProps = {
                          drop,
                          costPerSms,
                          totalCost,
                          isSelected: selectedSet.has(drop.id),
                          isBusy: isBusy || bulkSendStatus.running,
                          isSending: sendingDropId === drop.id,
                          isKilling: killingDropIds.includes(drop.id),
                          gatewayName: catalog.gatewayName(drop) || `#${drop.routeId}`,
                          leadsListName: catalog.leadsListName(drop) || `#${drop.leadsListWiserId}`,
                          leadsListTags: tagsForDrop(drop),
                          tagColors,
                          returnTo: listBackTo,
                          showCountryColumn,
                          creativeFilling,
                          rowGridStyle: draftRowGridStyle,
                          onToggleSelected: toggleSelected,
                          onDraftRowPointerDown,
                          onDraftRowPointerEnter,
                          consumeDraftRowClickSuppress,
                          onEdit: (id: string | null) => {
                            if (id) {
                              toggleSelected(id, true);
                              requestOpenBulkEdit();
                            }
                          },
                          onSend,
                          onEditSent: onSaveSentEdit
                            ? async (d: import('@/features/smswiser-campaigns/types/workspace.types').PreparedDrop) => {
                                if (onBeforeEditSent) {
                                  const ok = await onBeforeEditSent(d);
                                  if (!ok) return;
                                }
                                setEditingSentDropIds([d.id]);
                              }
                            : undefined,
                        };
                        return drop.status === 'DRAFT' ? (
                          <SortableDraftRow key={drop.id} {...rowProps} />
                        ) : (
                          <PreparedDropsDraftsListRow key={drop.id} {...rowProps} />
                        );
                      })}
                    </>
                  </SortableContext>
                  <DragOverlay dropAnimation={null}>
                    {activeDragId ? (() => {
                      const activeDrop = state.displayedDrops.find((d) => d.id === activeDragId);
                      if (!activeDrop || activeDrop.status !== 'DRAFT') return null;
                      const op = catalog.gatewayPricePerSms(activeDrop);
                      const costPerSms = op > 0 ? money(op) : null;
                      const totalCost = op > 0 ? money(activeDrop.amount * op) : null;
                      return (
                        <DraftRowOverlay
                          drop={activeDrop}
                          costPerSms={costPerSms}
                          totalCost={totalCost}
                          isSelected={state.selectedSet.has(activeDrop.id)}
                          isBusy={isBusy || state.bulkSendStatus.running}
                          isSending={sendingDropId === activeDrop.id}
                          isKilling={killingDropIds.includes(activeDrop.id)}
                          gatewayName={catalog.gatewayName(activeDrop) || `#${activeDrop.routeId}`}
                          leadsListName={catalog.leadsListName(activeDrop) || `#${activeDrop.leadsListWiserId}`}
                          leadsListTags={tagsForDrop(activeDrop)}
                          tagColors={tagColors}
                          returnTo={listBackTo}
                        showCountryColumn={showCountryColumn}
                        rowGridStyle={draftRowGridStyle}
                        onToggleSelected={state.toggleSelected}
                        onDraftRowPointerDown={onDraftRowPointerDown}
                        onDraftRowPointerEnter={onDraftRowPointerEnter}
                        consumeDraftRowClickSuppress={consumeDraftRowClickSuppress}
                        onEdit={(id: string | null) => {
                            if (id) {
                              state.toggleSelected(id, true);
                              requestOpenBulkEdit();
                            }
                          }}
                          onSend={onSend}
                          onEditSent={
                            onSaveSentEdit
                              ? async (d: import('@/features/smswiser-campaigns/types/workspace.types').PreparedDrop) => {
                                  if (onBeforeEditSent) {
                                    const ok = await onBeforeEditSent(d);
                                    if (!ok) return;
                                  }
                                  setEditingSentDropIds([d.id]);
                                }
                              : undefined
                          }
                        />
                      );
                    })() : null}
              </DragOverlay>
            </DndContext>
          ) : (
                <>
                  {displayedDrops.map((drop) => {
                    const pr = catalog.gatewayPricePerSms(drop);
                    const costPerSms = pr > 0 ? money(pr) : null;
                    const totalCost = pr > 0 ? money(drop.amount * pr) : null;
                    return (
                      <PreparedDropsDraftsListRow
                        key={drop.id}
                        drop={drop}
                        costPerSms={costPerSms}
                        totalCost={totalCost}
                        isSelected={selectedSet.has(drop.id)}
                        isBusy={isBusy || bulkSendStatus.running}
                        isSending={sendingDropId === drop.id}
                        isKilling={killingDropIds.includes(drop.id)}
                        gatewayName={catalog.gatewayName(drop) || `#${drop.routeId}`}
                        leadsListName={catalog.leadsListName(drop) || `#${drop.leadsListWiserId}`}
                        leadsListTags={tagsForDrop(drop)}
                        tagColors={tagColors}
                        returnTo={listBackTo}
                        showCountryColumn={showCountryColumn}
                        rowGridStyle={draftRowGridStyle}
                        onToggleSelected={toggleSelected}
                        onDraftRowPointerDown={onDraftRowPointerDown}
                        onDraftRowPointerEnter={onDraftRowPointerEnter}
                        consumeDraftRowClickSuppress={consumeDraftRowClickSuppress}
                        onEdit={(id) => {
                          if (id) {
                            toggleSelected(id, true);
                            requestOpenBulkEdit();
                          }
                        }}
                        onSend={onSend}
                        onEditSent={
                          onSaveSentEdit
                            ? async (d) => {
                                if (onBeforeEditSent) {
                                  const ok = await onBeforeEditSent(d);
                                  if (!ok) return;
                                }
                                setEditingSentDropIds([d.id]);
                              }
                            : undefined
                        }
                        />
                      );
                    })}
                </>
          )}
            </div>
          )}
        </div>
      </div>

      {state.showCopyAsDraft && onCopyAsDraft ? (
        <CopyAsDraftModal
          count={selectedSentDrops.length}
          defaultDate={workDate}
          isBusy={isBusy}
          onClose={() => setShowCopyAsDraft(false)}
          onConfirm={(targetWorkDate) => handleCopyAsDraftConfirm(targetWorkDate)}
        />
      ) : null}

      {state.showDuplicateDraft && onDuplicateDraft ? (
        <DuplicateDraftModal
          count={selectedIds.length}
          sources={draftDrops
            .filter((d) => selectedIds.includes(d.id))
            .map((d) => ({ workDate: d.workDate, executeAfter: d.executeAfter }))}
          defaultDate={workDate}
          isBusy={isBusy}
          onClose={() => setShowDuplicateDraft(false)}
          onConfirm={handleDuplicateDraftConfirm}
        />
      ) : null}

      {applyCopiedPreviewOpen && stagedApplyPayload ? (
        <ApplyCopiedConfigSentPreviewModal
          open={applyCopiedPreviewOpen}
          payload={stagedApplyPayload}
          workspaceWiserId={wiserId ?? selectedWithSentId[0]?.wiserId ?? 0}
          campaigns={campaigns}
          gateways={gateways.map((g) => ({ id: g.id, name: g.name }))}
          selectedDropCount={selectedWithSentId.length}
          onClose={() => {
            setApplyCopiedPreviewOpen(false);
            setStagedApplyPayload(null);
          }}
          onConfirmEdit={handleApplyCopiedConfirmEdit}
        />
      ) : null}

      {editingSentDrops.length > 0 && onSaveSentEdit ? (
        <EditSentDropModal
          countryKey={editModalCountryKey}
          drops={editingSentDrops}
          wiserId={wiserId ?? editingSentDrops[0]?.wiserId ?? 0}
          gateways={editModalGateways.map((g) => ({ id: g.id, name: g.name }))}
          tlds={tlds.map((t) => ({ value: t.value, displayName: t.displayName }))}
          senderNames={creative.senderNames}
          smsTexts={creative.smsTexts}
          isBusy={isBusy}
          onClose={() => {
            setEditingSentDropIds([]);
            setInitialApplyForEditModal(null);
          }}
          onAddSender={onAddSender}
          onAddText={onAddText}
          onDeleteText={onDeleteText}
          onDeleteTextsBulk={onDeleteTextsBulk}
          onSave={(payload) => onSaveSentEdit(payload)}
          initialApplyPayload={initialApplyForEditModal}
          onInitialApplyConsumed={() => setInitialApplyForEditModal(null)}
          tagsByTextId={tagsByTextId}
          tagColors={textPerformanceTagColors}
          smsTextsWithIds={creative.smsTextsWithIds}
        />
      ) : null}

      {multiCountrySentEditIds.length > 0 && onSaveSentEdit ? (
        <MultiCountrySentScheduledEditModal
          selectedIds={multiCountrySentEditIds}
          mergedDrops={props.drops}
          catalogByCountry={multiCountryCatalog}
          senderNames={creative.senderNames}
          smsTexts={creative.smsTexts}
          onClose={() => setMultiCountrySentEditIds([])}
          onSave={(payloads, meta) => onSaveSentEdit(payloads, meta)}
        />
      ) : null}

      {showMultiCountryEdit && selectedIds.length > 0 ? (
        <MultiCountryBulkEditModal
          selectedIds={selectedIds}
          mergedDrops={props.drops}
          tlds={(props.tlds ?? []).map((t) => ({ value: t.value, displayName: t.displayName }))}
          catalogByCountry={multiCountryCatalog}
          senderNames={creative.senderNames}
          smsTexts={creative.smsTexts}
          onClose={() => setShowMultiCountryEdit(false)}
          onSave={async ({ items, payload }) => {
            await onBulkUpdate(items.map((i) => i.id), payload);
          }}
        />
      ) : null}

      {state.showBulkEdit && selectedIds.length > 0 ? (
        <ScaleDropModal
          countryKey={scaleModalCountryKey}
          perfTagsQuery={perfTagsQuery}
          rows={draftDrops
            .filter((d) => selectedIds.includes(d.id))
            .map((d) =>
              preparedDropToDropsWithStatsRow(
                d,
                modalGateways.map((g) => ({ id: g.id, name: g.name, pricePerSms: g.pricePerSms })),
                modalLeadsLists.map((l) => ({ id: l.id, name: l.name, count: l.count })),
              ),
            )}
          gateways={modalGateways}
          campaigns={campaigns}
          editDrops={
            selectedIds.length > 0
              ? draftDrops
                  .filter((d) => selectedIds.includes(d.id))
                  .map((d) => ({
                    routeId: d.routeId,
                    wiserId: d.wiserId,
                    shortLinkTldValue: d.shortLinkTldValue,
                    amount: d.amount,
                    leadsListWiserId: d.leadsListWiserId,
                    executeAfter: d.executeAfter ?? null,
                    senderNames: d.senderNames ?? [],
                    smsTexts: d.smsTexts ?? [],
                    batchAmount: d.batchAmount ?? null,
                    batchDelay: d.batchDelay ?? null,
                    forceSend: d.forceSend ?? false,
                    aiOptimizationEnabled: d.aiOptimizationEnabled ?? false,
                    aiOptimizationType:
                      d.aiOptimizationType === 'ROI' || d.aiOptimizationType === 'CTR'
                        ? d.aiOptimizationType
                        : null,
                    aiOptimizationBatchAmount: d.aiOptimizationBatchAmount ?? null,
                    aiOptimizationMinimalValuePercent: d.aiOptimizationMinimalValuePercent ?? null,
                    aiOptimizationSendUntil: d.aiOptimizationSendUntil ?? null,
                    useOnlyNewDomains: d.useOnlyNewDomains ?? false,
                    clickIdStructure: d.clickIdStructure ?? null,
                    domainScheme: d.domainScheme ?? null,
                    staticTagId: d.staticTagId ?? null,
                  }))
              : undefined
          }
          leadsLists={modalLeadsLists}
          tlds={tlds}
          senderNames={creative.senderNames}
          smsTexts={creative.smsTexts}
          smsTextsWithIds={creative.smsTextsWithIds}
          defaultWorkDate={workDate}
          titlePrefix=""
          isBusy={isBusy}
          mode="bulk-edit"
          editDropIds={selectedIds}
          onClose={() => setShowBulkEdit(false)}
          onAddSender={onAddSender}
          onAddText={onAddText}
          onDeleteSender={onDeleteSender}
          onDeleteText={onDeleteText}
          onDeleteTextsBulk={onDeleteTextsBulk}
          onBulkSave={(ids, payload, scheduleIntervalMinutes) => onBulkUpdate(ids, payload, scheduleIntervalMinutes)}
          onBulkSaveAndSend={onBulkSaveAndSend}
        />
      ) : null}

      <ConfirmDialog
        open={killConfirmOpen}
        title="Stop these drops?"
        destructive
        description={`Stopping ${selectedWithSentId.length} drop${selectedWithSentId.length === 1 ? '' : 's'} cannot be undone — SMSWiser has no way to restart a stopped drop, and anything already sent cannot be recalled.`}
        confirmLabel="Stop drops"
        busy={isBusy}
        onConfirm={() => {
          setKillConfirmOpen(false);
          void handleKillSent();
        }}
        onCancel={() => setKillConfirmOpen(false)}
      />
    </div>
  );
};

export default PreparedDropsDraftsList;
